import {
  useEffect,
  useRef,
  useState,
  type DragEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import { nowMinutes, today, weekdayLabel, type CalendarCell } from '@/tasks/calendar-range';
import { groupByDueDate, type Task } from '@/tasks/task';
import {
  DAY_HEIGHT_PX,
  DEFAULT_SCROLL_HOUR,
  formatClockLabel,
  formatEventTimeRange,
  formatHourLabel,
  formatTime,
  HOUR_HEIGHT_PX,
  HOURS,
  layoutCalendarEvents,
  MINUTE_HEIGHT_PX,
  resizeTask,
  SLOT_HEIGHT_PX,
  toDraftRange,
  splitTimedTasks,
  taskDurationMinutes,
  toSlotMinutes,
  type DraftRange,
  type DropTarget,
  type ResizeEdge,
} from '@/tasks/time-grid';
import { eventStyle, EVENT_CLASS } from './KindLabel';

/**
 * 時間軸の背景（横罫）。
 *
 * 横線は 1 時間ごとだけ引く。予定の位置と長さは 15 分刻みで置くが、15 分ごとに線を
 * 引くと罫線が主役になって予定が読みにくくなるため。要素を 24 個並べる代わりに
 * 背景の繰り返しで描き、タスクの重なり計算と DOM を分けておく。
 * @graph-connects none
 */
const GRID_BACKGROUND = `repeating-linear-gradient(to bottom, var(--color-cal-line) 0px, var(--color-cal-line) 1px, transparent 1px, transparent ${String(HOUR_HEIGHT_PX)}px)`;

/** 時刻の目盛り列の幅 */
/** @graph-connects none */
const GUTTER_WIDTH = '3.75rem';

/** これより低いチップは 1 行に収める（Google カレンダーと同じく、30 分以下の予定は「内容、午前10:30」と横に並べる） */
/** @graph-connects none */
const COMPACT_HEIGHT_PX = 30;

/** 現在時刻の線を引き直す間隔 */
/** @graph-connects none */
const NOW_TICK_MS = 60_000;

/**
 * TimeGridCalendar の props。
 */
export interface TimeGridCalendarProps {
  /** 並べる日。週なら 7 日、日なら 1 日 */
  cells: readonly CalendarCell[];
  /** 表示するタスク */
  tasks: readonly Task[];
  /** タスクを選んだときに呼ばれる */
  onSelect?: (task: Task) => void;
  /** タスクを別の日時へ動かしたときに呼ばれる */
  onMove?: (task: Task, target: DropTarget) => void;
  /** 端をつまんで時間の長さを変えたときに呼ばれる */
  onResize?: (task: Task) => void;
  /** 空いているところを引いて、新しい予定を作ったときに呼ばれる */
  onCreate?: (range: DraftRange) => void;
}

/**
 * @graph-node {Page}
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business タスクを 15 分刻みの時間軸カレンダーに並べる。時刻を持たないタスクは上部の終日の帯に置く
 * @graph-connects layoutCalendarEvents [calls] 時刻を持つタスクの縦位置と、横並び・重ね方を決める
 * @graph-connects splitTimedTasks [calls] 終日と時刻ありに分ける
 * @graph-connects groupByDueDate [calls] 日ごとにタスクをまとめる
 * @graph-connects toSlotMinutes [calls] 落とした位置を 15 分刻みの時刻に丸める
 * @graph-connects taskDurationMinutes [calls] 動かすタスクの長さを決める
 * @graph-connects resizeTask [calls] 端をつまんだときの時刻を決める
 * @graph-connects formatTime [calls] 同じ場所へ落としたかの判定に使う
 * @graph-connects eventStyle [calls] 予定の面の色を引く
 * @graph-connects formatEventTimeRange [calls] 予定の時間帯を午前・午後の表記にする
 * @graph-connects nowMinutes [calls] 現在時刻の線の位置を決める
 */
export function TimeGridCalendar({
  cells,
  tasks,
  onSelect,
  onMove,
  onResize,
  onCreate,
}: TimeGridCalendarProps) {
  const byDate = groupByDueDate(tasks);
  const scrollRef = useRef<HTMLDivElement>(null);
  // 掴んでいるタスク。DataTransfer だけに頼ると、落とす瞬間まで中身を読めない
  const draggingRef = useRef<Task | null>(null);
  // 落とし先の下見。掴んだまま重ねている場所を薄く示す
  const [hover, setHover] = useState<DropTarget | null>(null);
  // 端をつまんでいる最中のタスク。動かしている間だけ持つ
  const [resizing, setResizing] = useState<{ task: Task; edge: ResizeEdge } | null>(null);
  // 今日の日付と現在時刻。Google カレンダーと同じく、今日の見出しを塗り、いまの時刻に線を引く
  const [clock, setClock] = useState(() => ({ date: today(), minutes: nowMinutes() }));
  useEffect(() => {
    const timer = window.setInterval(() => {
      setClock({ date: today(), minutes: nowMinutes() });
    }, NOW_TICK_MS);
    return () => {
      window.clearInterval(timer);
    };
  }, []);

  const startDrag = (task: Task) => (event: DragEvent<HTMLElement>) => {
    draggingRef.current = task;
    event.dataTransfer.effectAllowed = 'move';
    // Firefox は何か入れないとドラッグが始まらない
    event.dataTransfer.setData('text/plain', task.id);
  };

  /**
   * 落とし先の下見を更新する。
   *
   * `dragOver` は指を止めていても数ミリ秒ごとに飛んでくるので、そのたびに state を
   * 入れ替えるとカレンダー全体が描き直されて画面が揺れる。位置が実際に変わった
   * ときだけ差し替える
   */
  const updateHover = (next: DropTarget | null) => {
    setHover((current) => {
      if (current === null && next === null) {
        return current;
      }
      if (
        current !== null &&
        next !== null &&
        current.date === next.date &&
        current.startMinutes === next.startMinutes
      ) {
        return current;
      }
      return next;
    });
  };

  const endDrag = () => {
    draggingRef.current = null;
    updateHover(null);
  };

  const drop = (target: DropTarget) => {
    const task = draggingRef.current;
    endDrag();
    if (task === null || onMove === undefined) {
      return;
    }
    // 同じ場所へ落としたときは何もしない（無駄な保存で更新履歴が埋まる）
    const sameDate = task.dueDate === target.date;
    const sameStart =
      target.startMinutes === undefined
        ? task.startTime === undefined
        : formatTime(target.startMinutes) === task.startTime;
    if (sameDate && sameStart) {
      return;
    }
    onMove(task, target);
  };

  /** 時間軸の列に落としたときの、掴んでいる位置から開始時刻を決める */
  const toTimedTarget = (date: string, event: DragEvent<HTMLElement>): DropTarget => {
    const bounds = event.currentTarget.getBoundingClientRect();
    const duration =
      draggingRef.current === null ? undefined : taskDurationMinutes(draggingRef.current);
    // 掴んでいる位置が取れないことがある（端末やブラウザによる）。その場合は 0 時に置く
    const offset = Number.isFinite(event.clientY) ? event.clientY - bounds.top : 0;
    return { date, startMinutes: toSlotMinutes(offset, duration) };
  };

  // 空いているところを引いている最中の時間帯。離すまで下書きとして描く
  const [drafting, setDrafting] = useState<DraftRange | null>(null);

  /**
   * 空いているところを押して引くと、その時間帯で新しい予定を作る。
   * 予定の上で押したときは動かす操作なので、ここには来ない（チップ側で止める）
   */
  const startDraft = (date: string) => (event: ReactPointerEvent<HTMLElement>) => {
    // 右クリックや中クリックでは始めない（左ボタンは 0）
    if (onCreate === undefined || event.button > 0) {
      return;
    }
    const column = event.currentTarget;
    const top = column.getBoundingClientRect().top;
    // 押した位置が読めない環境では 0 時から始める（時刻を壊さない）
    const from = Number.isFinite(event.clientY) ? event.clientY - top : 0;
    let latest = toDraftRange(date, from, from);
    setDrafting(latest);

    const move = (moveEvent: PointerEvent) => {
      if (!Number.isFinite(moveEvent.clientY)) {
        return;
      }
      latest = toDraftRange(date, from, moveEvent.clientY - top);
      setDrafting(latest);
    };
    const finish = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', finish);
      setDrafting(null);
      onCreate(latest);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', finish);
  };

  /**
   * 端をつまんで動かす。列の上端からの距離で時刻を決めるので、押した時点で
   * 列の位置を覚えておき、離すまでその位置を基準にする
   */
  const startResize = (task: Task, edge: ResizeEdge) => (event: ReactPointerEvent<HTMLElement>) => {
    if (onResize === undefined) {
      return;
    }
    // 親のドラッグ（予定ごと動かす）と取り合いにならないよう、ここで止める
    event.preventDefault();
    event.stopPropagation();

    const column = event.currentTarget.closest('[data-drop-date]');
    if (column === null) {
      return;
    }
    const top = column.getBoundingClientRect().top;
    setResizing({ task, edge });

    let latest = task;
    const move = (moveEvent: PointerEvent) => {
      // 位置が読めない環境では動かさない（時刻を壊さない）
      if (!Number.isFinite(moveEvent.clientY)) {
        return;
      }
      latest = resizeTask(task, edge, moveEvent.clientY - top);
      setResizing({ task: latest, edge });
    };
    const finish = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', finish);
      setResizing(null);
      // 変わっていなければ保存しない（無駄な更新で履歴が埋まる）
      if (latest.startTime !== task.startTime || latest.endTime !== task.endTime) {
        onResize(latest);
      }
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', finish);
  };

  // 0 時から見せると空欄しか映らないので、始業前後まで送っておく
  useEffect(() => {
    if (scrollRef.current !== null) {
      scrollRef.current.scrollTop = DEFAULT_SCROLL_HOUR * HOUR_HEIGHT_PX;
    }
  }, []);

  const columns = `${GUTTER_WIDTH} repeat(${String(cells.length)}, minmax(0, 1fr))`;
  const hasAllDay = cells.some(
    (cell) => splitTimedTasks(byDate.get(cell.date) ?? []).allDay.length > 0,
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-x-auto overflow-y-hidden rounded-2xl bg-[var(--color-bg-surface)]">
      <div
        className={`flex min-h-0 flex-1 flex-col ${cells.length > 1 ? 'min-w-[720px]' : 'min-w-[320px]'}`}
      >
        {/* 日付の見出し */}
        <div className="grid shrink-0" style={{ gridTemplateColumns: columns }}>
          {/* Google カレンダーと同じく、目盛りの列の上にタイムゾーンを出す */}
          <div className="flex items-end justify-end pr-2 pb-1 text-[10px] text-[var(--color-cal-label)]">
            GMT+09
          </div>
          {cells.map((cell) => {
            const isToday = cell.date === clock.date;
            return (
              <div
                key={cell.date}
                aria-current={isToday ? 'date' : undefined}
                className="flex flex-col items-center pt-1.5 pb-1 leading-tight"
              >
                {/* Google カレンダーと同じく、曜日を小さく上に、日付を大きく下に置く。今日は日付を丸で塗る */}
                <div
                  className={`text-[11px] font-medium ${isToday ? 'text-[var(--color-cal-today)]' : 'text-[var(--color-cal-label)]'}`}
                >
                  {weekdayLabel(cell.date)}
                </div>
                <div
                  className={`mt-1 flex h-11 w-11 items-center justify-center rounded-full text-[26px] tabular-nums ${
                    isToday
                      ? 'bg-[var(--color-cal-today)] text-white'
                      : 'text-[#3c4043] hover:bg-[var(--color-bg-subtle)]'
                  }`}
                >
                  {cell.day}
                </div>
              </div>
            );
          })}
        </div>

        {/* 終日の帯。時刻の無いタスクはここへ置く（時間軸に置き場所がない）。
            月初は 1 日に 20 件以上並ぶので、帯の高さは決め打ちにする。スクロールは
            日ごとに持たせる（帯ごとスクロールさせると、件数の多い日に合わせて
            他の日の空欄まで一緒に流れてしまう） */}
        {hasAllDay ? (
          <div
            className="grid shrink-0 border-b border-[var(--color-cal-line)]"
            style={{ gridTemplateColumns: columns }}
          >
            <div className="py-1 pr-2 text-right text-[10px] text-[var(--color-cal-label)]">
              終日
            </div>
            {cells.map((cell) => (
              <div
                key={cell.date}
                data-drop-date={`allday-${cell.date}`}
                onDragOver={(event) => {
                  if (onMove !== undefined) {
                    event.preventDefault();
                    updateHover({ date: cell.date });
                  }
                }}
                onDragLeave={() => {
                  updateHover(null);
                }}
                onDrop={(event) => {
                  event.preventDefault();
                  drop({ date: cell.date });
                }}
                className={`max-h-40 space-y-px overflow-y-auto border-l border-[var(--color-cal-line)] pr-2 pl-0.5 py-0.5 ${
                  hover?.date === cell.date && hover.startMinutes === undefined
                    ? 'bg-[var(--color-doing-bg)]'
                    : ''
                }`}
              >
                {splitTimedTasks(byDate.get(cell.date) ?? []).allDay.map((task) => (
                  <button
                    key={task.id}
                    type="button"
                    draggable={onMove !== undefined}
                    onDragStart={startDrag(task)}
                    onDragEnd={endDrag}
                    title={`${task.kind}／${task.assignee}／${task.title}`}
                    onClick={() => {
                      onSelect?.(task);
                    }}
                    style={eventStyle(task)}
                    className={`block w-full truncate rounded px-2 py-px text-left text-xs font-medium ${EVENT_CLASS[task.status]}`}
                  >
                    {task.title}
                  </button>
                ))}
              </div>
            ))}
          </div>
        ) : null}

        {/* 時間軸。残りの高さいっぱいに広げ、中だけを縦スクロールさせる（見出しと終日は残す） */}
        <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto">
          <div className="grid" style={{ gridTemplateColumns: columns }}>
            {/* 時刻の目盛り */}
            <div className="relative" style={{ height: `${String(DAY_HEIGHT_PX)}px` }}>
              {/* Google カレンダーと同じく、目盛りは罫線の高さに揃えて置く（0 時は上端に重なるので出さない） */}
              {HOURS.filter((hour) => hour > 0).map((hour) => (
                <div
                  key={hour}
                  className="absolute right-2 -translate-y-1/2 text-[10px] tabular-nums text-[var(--color-cal-label)]"
                  style={{ top: `${String(hour * HOUR_HEIGHT_PX)}px` }}
                >
                  {formatHourLabel(hour)}
                </div>
              ))}
            </div>

            {cells.map((cell) => {
              const { timed } = splitTimedTasks(byDate.get(cell.date) ?? []);
              return (
                <div
                  key={cell.date}
                  data-drop-date={`timed-${cell.date}`}
                  onPointerDown={startDraft(cell.date)}
                  onDragOver={(event) => {
                    if (onMove !== undefined) {
                      event.preventDefault();
                      updateHover(toTimedTarget(cell.date, event));
                    }
                  }}
                  onDragLeave={() => {
                    updateHover(null);
                  }}
                  onDrop={(event) => {
                    event.preventDefault();
                    drop(toTimedTarget(cell.date, event));
                  }}
                  className="relative border-l border-[var(--color-cal-line)]"
                  style={{
                    height: `${String(DAY_HEIGHT_PX)}px`,
                    backgroundImage: GRID_BACKGROUND,
                  }}
                >
                  {/* 現在時刻の線。Google カレンダーと同じく、今日の列にだけ赤い線と丸を引く */}
                  {cell.date === clock.date ? (
                    <div
                      aria-hidden="true"
                      data-now-line
                      className="pointer-events-none absolute inset-x-0 z-10"
                      style={{ top: `${String(clock.minutes * MINUTE_HEIGHT_PX)}px` }}
                    >
                      <div className="absolute -top-[5px] -left-[6px] h-3 w-3 rounded-full bg-[var(--color-now)]" />
                      <div className="h-0.5 -translate-y-1/2 bg-[var(--color-now)]" />
                    </div>
                  ) : null}

                  {/* 引いている最中の下書き。離すとこの時間帯で予定を作る */}
                  {drafting?.date === cell.date ? (
                    <div
                      aria-hidden="true"
                      style={{
                        top: `${String(drafting.startMinutes * MINUTE_HEIGHT_PX)}px`,
                        height: `${String(
                          Math.max(
                            (drafting.endMinutes - drafting.startMinutes) * MINUTE_HEIGHT_PX,
                            SLOT_HEIGHT_PX,
                          ),
                        )}px`,
                      }}
                      className="pointer-events-none absolute inset-x-0.5 flex items-start rounded border border-[var(--color-accent)] bg-[var(--color-doing-bg)] px-1.5 text-xs tabular-nums text-[var(--color-doing-text)]"
                    >
                      {formatTime(drafting.startMinutes)}〜{formatTime(drafting.endMinutes)}
                    </div>
                  ) : null}

                  {/* 落とす場所の下見。掴んだまま重ねている位置に、置いたときの
                      大きさで薄い枠を出す */}
                  {hover?.date === cell.date && hover.startMinutes !== undefined ? (
                    <div
                      aria-hidden="true"
                      style={{
                        top: `${String(hover.startMinutes * MINUTE_HEIGHT_PX)}px`,
                        height: `${String(
                          Math.max(
                            (draggingRef.current === null
                              ? 30
                              : taskDurationMinutes(draggingRef.current)) * MINUTE_HEIGHT_PX,
                            SLOT_HEIGHT_PX,
                          ),
                        )}px`,
                      }}
                      className="pointer-events-none absolute inset-x-0.5 rounded border border-dashed border-[var(--color-accent)] bg-[var(--color-doing-bg)]/60"
                    />
                  ) : null}
                  {layoutCalendarEvents(
                    // つまんでいる最中は、手元の時刻で描く（離すまで保存しない）
                    resizing === null
                      ? timed
                      : timed.map((entry) =>
                          entry.id === resizing.task.id ? resizing.task : entry,
                        ),
                  ).map(({ task, startMinutes, endMinutes, left, width, depth }) => {
                    const height = (endMinutes - startMinutes) * MINUTE_HEIGHT_PX;
                    const compact = height < COMPACT_HEIGHT_PX;
                    return (
                      <button
                        key={task.id}
                        type="button"
                        title={`${formatEventTimeRange(task)}／${task.kind}／${task.assignee}／${task.title}`}
                        onClick={() => {
                          onSelect?.(task);
                        }}
                        draggable={onMove !== undefined}
                        onDragStart={startDrag(task)}
                        onDragEnd={endDrag}
                        onPointerDown={(event) => {
                          // 予定の上で押したときは動かす操作。列の「引いて作る」を始めない
                          event.stopPropagation();
                        }}
                        style={{
                          top: `${String(startMinutes * MINUTE_HEIGHT_PX)}px`,
                          height: `${String(Math.max(height, SLOT_HEIGHT_PX))}px`,
                          left: `calc(${String(left)}% + 1px)`,
                          // 右端は Google カレンダーと同じく少し空け、空いているところを押して予定を作れるようにする
                          width: `calc(${String(width)}% - ${left + width >= 99.9 ? '10px' : '2px'})`,
                          zIndex: depth + 1,
                          // 対応済にした予定だけ淡くする（時間が過ぎただけでは薄くしない）
                          ...eventStyle(task),
                        }}
                        className={`group absolute overflow-hidden rounded-md border border-white px-2 text-left text-xs leading-tight ${EVENT_CLASS[task.status]} ${compact ? 'flex items-center py-0' : 'flex flex-col justify-start py-0.5'}`}
                      >
                        {/* Google カレンダーと同じく、内容を先に太めに出し、時間帯を添える。
                            短い予定は「内容、午前10:30」と 1 行に並べる */}
                        {compact ? (
                          <span className="block truncate">
                            <span className="font-medium">{task.title}</span>
                            {`、${formatClockLabel(startMinutes)}`}
                          </span>
                        ) : (
                          <>
                            <span className="block truncate font-medium">{task.title}</span>
                            <span className="block truncate tabular-nums">
                              {formatEventTimeRange(task)}
                            </span>
                          </>
                        )}

                        {/* 上端・下端のつまみ。ふだんは見せず、重ねたときだけ出す
                            （細い帯が常に見えていると予定そのものが読みにくい） */}
                        {onResize === undefined ? null : (
                          <>
                            <span
                              aria-hidden="true"
                              data-resize={`start-${task.id}`}
                              onPointerDown={startResize(task, 'start')}
                              className="absolute inset-x-0 top-0 h-1.5 cursor-ns-resize opacity-0 group-hover:bg-[var(--color-accent)] group-hover:opacity-40"
                            />
                            <span
                              aria-hidden="true"
                              data-resize={`end-${task.id}`}
                              onPointerDown={startResize(task, 'end')}
                              className="absolute inset-x-0 bottom-0 h-1.5 cursor-ns-resize opacity-0 group-hover:bg-[var(--color-accent)] group-hover:opacity-40"
                            />
                          </>
                        )}
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
